import type {
  FlowSchema,
  FlowStep,
  FlowStepEnd,
  FlowStepLeadGate,
  FlowStepRadiogroup,
  FlowStepSkinScan,
} from './flow-schema.js';

export type FlowCompleteMeta = {
  flowId?: string;
  flowVersion?: number;
  endStep: FlowStepEnd | null;
};

export function isFlowSchemaV2(schema: unknown): schema is FlowSchema {
  return (
    !!schema &&
    typeof schema === 'object' &&
    (schema as FlowSchema).version === 2 &&
    Array.isArray((schema as FlowSchema).steps)
  );
}

export function getEndStep(schema: FlowSchema): FlowStepEnd | null {
  const end = schema.steps.find((s) => s.type === 'end');
  return end?.type === 'end' ? end : null;
}

export function prepareFlowForSurvey(
  schema: unknown,
): { surveyJson: Record<string, unknown>; endStep: FlowStepEnd | null } | null {
  if (isFlowSchemaV2(schema)) {
    const compiled = compileToSurveyJS(schema);
    if (!compiled) return null;
    return { surveyJson: compiled, endStep: getEndStep(schema) };
  }
  if (schema && typeof schema === 'object' && 'pages' in (schema as object)) {
    return { surveyJson: schema as Record<string, unknown>, endStep: null };
  }
  return null;
}

export type InteractiveFlowStep = Exclude<FlowStep, FlowStepEnd>;

export function interactiveFlowSteps(schema: FlowSchema): InteractiveFlowStep[] {
  return schema.steps.filter((step): step is InteractiveFlowStep => step.type !== 'end');
}

/** Hide steps jumped by radiogroup `branching.skipTo` (same window as SurveyJS `visibleIf`). */
export function isFlowStepVisible(
  schema: FlowSchema,
  stepId: string,
  answers: Record<string, unknown>,
): boolean {
  const steps = schema.steps;
  const targetIdx = steps.findIndex((step) => step.id === stepId);
  if (targetIdx < 0) return false;
  if (steps[targetIdx]?.type === 'end') return false;

  for (let i = 0; i < targetIdx; i++) {
    const step = steps[i];
    if (step.type !== 'radiogroup' || !step.branching) continue;
    const chosen = answers[step.id];
    if (typeof chosen !== 'string') continue;
    const rule = step.branching[chosen];
    if (!rule) continue;
    const skipToIdx = steps.findIndex((s) => s.id === rule.skipTo);
    if (skipToIdx > i + 1 && targetIdx > i && targetIdx < skipToIdx) {
      return false;
    }
  }
  return true;
}

export function visibleFlowSteps(
  schema: FlowSchema,
  answers: Record<string, unknown>,
): InteractiveFlowStep[] {
  return interactiveFlowSteps(schema).filter((step) =>
    isFlowStepVisible(schema, step.id, answers),
  );
}

function isSkinScanComplete(value: unknown): boolean {
  return Boolean(
    value &&
      typeof value === 'object' &&
      typeof (value as { completedAt?: unknown }).completedAt === 'string',
  );
}

function hasText(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

/** Choice questions always need a value. Other types honor `required`. */
export function flowStepCanProceed(
  step: FlowStep,
  answers: Record<string, unknown>,
): boolean {
  switch (step.type) {
    case 'end':
    case 'html':
      return true;
    case 'radiogroup':
      return hasText(answers[step.id]);
    case 'imagepicker': {
      if (step.multiSelect) {
        const value = answers[step.id];
        return Array.isArray(value) && value.length > 0;
      }
      return hasText(answers[step.id]);
    }
    case 'checkbox': {
      const value = answers[step.id];
      if (!Array.isArray(value) || value.length === 0) return step.required === false;
      return true;
    }
    case 'text': {
      if (step.required === false) return true;
      return hasText(answers[step.id]);
    }
    case 'rating': {
      if (step.required === false) return true;
      return typeof answers[step.id] === 'number';
    }
    case 'skin-scan':
      return isSkinScanComplete(answers[step.id]) || step.required === false;
    case 'lead-gate': {
      if (step.consentText && answers.consent !== true) return false;
      if (step.emailRequired !== false && step.fields.includes('email') && !hasText(answers.email)) {
        return false;
      }
      if (step.phoneRequired === true && !hasText(answers.phone)) return false;
      if (step.fields.includes('firstName') && !hasText(answers.firstName)) return false;
      if (step.fields.includes('lastName') && !hasText(answers.lastName)) return false;
      return true;
    }
    default:
      return true;
  }
}

export function flowCompleteLabel(schema: FlowSchema): string {
  return getEndStep(schema)?.actionType === 'routine-match' ? 'Voir ma routine' : 'Envoyer';
}

function escapeHtml(raw: string): string {
  return raw
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Stacked choice copy for SurveyJS without images (newline + CSS `::first-line`).
 */
function choiceSurveyText(label: string, description?: string): string {
  const title = label.trim();
  const desc = description?.trim();
  if (!desc) return title;
  return `${title}\n${desc}`;
}

/**
 * Landing-style choice card HTML (image + title + description).
 * Rendered via Survey `onTextMarkdown` passthrough on the storefront.
 */
export function choiceSurveyHtml(
  label: string,
  description?: string,
  imageUrl?: string,
): string {
  const title = escapeHtml(label.trim());
  const desc = description?.trim() ? escapeHtml(description.trim()) : '';
  const media = imageUrl?.trim()
    ? `<span class="lf-choice-card__media"><img class="lf-choice-card__img" src="${escapeHtml(imageUrl.trim())}" alt="" loading="lazy" decoding="async" /></span>`
    : '';
  const body =
    `<span class="lf-choice-card__body">` +
    `<span class="lf-choice-card__title">${title}</span>` +
    (desc ? `<span class="lf-choice-card__desc">${desc}</span>` : '') +
    `</span>`;
  return `<span class="lf-choice-card">${media}${body}</span>`;
}

/** Map `stepId::value` → illustration URL from a v2 flow schema. */
export function collectFlowChoiceIllustrations(
  schema: unknown,
): Record<string, { imageUrl: string; label: string }> {
  const out: Record<string, { imageUrl: string; label: string }> = {};
  if (!isFlowSchemaV2(schema)) return out;
  for (const step of schema.steps) {
    if (step.type !== 'radiogroup' && step.type !== 'checkbox' && step.type !== 'imagepicker') {
      continue;
    }
    for (const c of step.choices) {
      if (!c.imageUrl) continue;
      out[`${step.id}::${c.value}`] = { imageUrl: c.imageUrl, label: c.label };
    }
  }
  return out;
}

export function compileToSurveyJS(schema: FlowSchema): Record<string, unknown> | null {
  if (!schema?.steps?.length) return null;

  const surveyJson: Record<string, unknown> = {
    title: schema.meta?.title,
    description: schema.meta?.description,
    showProgressBar: 'top',
    progressBarType: 'questions',
    progressBarShowPageNumbers: false,
    progressBarShowPageTitles: false,
    showQuestionNumbers: 'off',
    questionsOnPageMode: 'questionPerPage',
    showNavigationButtons: true,
    showPrevButton: true,
    goNextPageAutomatic: false,
    allowCompleteSurveyAutomatic: false,
    firstPageIsStarted: false,
    startSurveyText: 'Commencer',
    pageNextText: 'Continuer',
    pagePrevText: 'Retour',
    completeText: 'Voir ma routine',
    pages: [] as Record<string, unknown>[],
  };

  const pages = surveyJson.pages as Record<string, unknown>[];

  for (const step of schema.steps) {
    if (step.type === 'end') continue;

    const page: Record<string, unknown> = {
      name: step.id,
      elements: [] as Record<string, unknown>[],
    };

    const element: Record<string, unknown> = {
      name: step.id,
      title: step.title,
      description: step.description,
      isRequired: step.required,
    };

    switch (step.type) {
      case 'radiogroup': {
        element.type = 'radiogroup';
        element.choices = step.choices.map((c) => ({
          value: c.value,
          text: c.imageUrl
            ? choiceSurveyHtml(c.label, c.description, c.imageUrl)
            : choiceSurveyText(c.label, c.description),
        }));
        break;
      }
      case 'checkbox': {
        element.type = 'checkbox';
        element.choices = step.choices.map((c) => ({
          value: c.value,
          text: c.imageUrl
            ? choiceSurveyHtml(c.label, c.description, c.imageUrl)
            : choiceSurveyText(c.label, c.description),
        }));
        if (typeof step.maxSelected === 'number' && step.maxSelected > 0) {
          element.maxSelectedChoices = step.maxSelected;
        }
        break;
      }
      case 'text':
        element.type = step.inputType === 'textarea' ? 'comment' : 'text';
        if (step.inputType === 'email') element.inputType = 'email';
        if (step.inputType === 'number') element.inputType = 'number';
        if (step.inputType === 'phone') element.inputType = 'tel';
        element.placeHolder = step.placeholder;
        break;
      case 'rating':
        element.type = 'rating';
        element.rateMin = step.rateMin;
        element.rateMax = step.rateMax;
        break;
      case 'imagepicker':
        element.type = 'imagepicker';
        element.choices = step.choices.map((c) => ({
          value: c.value,
          text: c.label,
          imageLink: c.imageUrl,
        }));
        element.multiSelect = step.multiSelect;
        break;
      case 'html':
        element.type = 'html';
        element.html = step.htmlContent;
        break;
      case 'skin-scan': {
        const scan = step as FlowStepSkinScan;
        element.type = 'skin-scan';
        element.mode = scan.mode ?? 'mock';
        element.captureDurationMs = scan.captureDurationMs ?? 3500;
        element.showMetrics = scan.showMetrics ?? ['hydration', 'texture', 'spots'];
        element.disclaimerHtml = scan.disclaimerHtml ?? '';
        break;
      }
      case 'lead-gate': {
        const gate = step as FlowStepLeadGate;
        element.type = 'panel';
        const panelElements: Record<string, unknown>[] = [];
        if (gate.fields?.includes('email')) {
          panelElements.push({
            type: 'text',
            inputType: 'email',
            name: 'email',
            title: 'Email',
            isRequired: gate.emailRequired !== false,
          });
        }
        if (gate.fields?.includes('phone')) {
          panelElements.push({
            type: 'text',
            inputType: 'tel',
            name: 'phone',
            title: 'Téléphone',
            isRequired: gate.phoneRequired === true,
          });
        }
        if (gate.fields?.includes('firstName')) {
          panelElements.push({
            type: 'text',
            name: 'firstName',
            title: 'Prénom',
            isRequired: true,
          });
        }
        if (gate.fields?.includes('lastName')) {
          panelElements.push({
            type: 'text',
            name: 'lastName',
            title: 'Nom',
            isRequired: true,
          });
        }
        if (gate.fields?.includes('name')) {
          panelElements.push({ type: 'text', name: 'name', title: 'Nom complet' });
        }
        if (gate.fields?.includes('company')) {
          panelElements.push({ type: 'text', name: 'company', title: 'Entreprise' });
        }
        if (gate.consentText) {
          panelElements.push({
            type: 'boolean',
            name: 'consent',
            title: gate.consentText,
            isRequired: true,
          });
        }
        element.elements = panelElements;
        element.title = gate.title;
        break;
      }
    }

    (page.elements as Record<string, unknown>[]).push(element);
    pages.push(page);
  }

  for (let i = 0; i < schema.steps.length; i++) {
    const step = schema.steps[i] as FlowStepRadiogroup;
    if (step.type !== 'radiogroup' || !step.branching) continue;

    for (const choiceVal of Object.keys(step.branching)) {
      const targetId = step.branching[choiceVal].skipTo;
      const targetIdx = schema.steps.findIndex((s) => s.id === targetId);

      if (targetIdx > i + 1) {
        for (let j = i + 1; j < targetIdx; j++) {
          const skippedStep = schema.steps[j];
          if (skippedStep.type === 'end') continue;

          const page = pages.find((p) => p.name === skippedStep.id);
          if (page) {
            const cond = `{${step.id}} != '${choiceVal}'`;
            page.visibleIf = page.visibleIf
              ? `(${page.visibleIf}) and (${cond})`
              : cond;
          }
        }
      }
    }
  }

  return surveyJson;
}
